/**
 * Combinatorial logic to optimize suggestions
 * Pure functions to find the best combination of items
 */

/**
 * Generate all possible combinations of items
 * @param {Array} items - The available items
 * @param {number} count - The number of items in each combination
 * @returns {Array<Array>} All possible combinations
 */
export function generateCombinations(items, count) {
  if (count === 0) return [[]];
  if (count > items.length) return [];
  if (count === items.length) return [items];

  const combinations = [];

  // Recursive combination generation
  function combine(start, combo) {
    if (combo.length === count) {
      combinations.push([...combo]);
      return;
    }

    for (let i = start; i < items.length; i++) {
      combo.push(items[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  }

  combine(0, []);
  return combinations;
}

/**
 * Find the best combination of items to fill the gap
 * @param {Array} availableItems - The available items
 * @param {number} itemsCount - The number of items to select
 * @param {number} gap - The gap to fill (remaining amount for free shipping)
 * @returns {Array|null} The best combination of items or null if none found
 */
export function findBestCombination(availableItems, itemsCount, gap) {
  // Limit available items to avoid combinatorial explosion
  // Take top items by score, or limit to a reasonable number
  const maxItemsToConsider = Math.min(10, availableItems.length);
  const itemsToConsider = availableItems
    .slice()
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, maxItemsToConsider);

  if (itemsToConsider.length < itemsCount) {
    return null;
  }

  // Generate all combinations
  const combinations = generateCombinations(itemsToConsider, itemsCount);

  if (combinations.length === 0) {
    return null;
  }

  // Calculate metrics for each combination
  const combinationsWithMetrics = combinations.map(combination => {
    const total = combination.reduce((sum, item) => sum + (item.price || 0), 0);
    const gapRemaining = gap - total;
    const avgScore = combination.reduce((sum, item) => sum + (item.score || 0), 0) / combination.length;
    
    return {
      combination,
      total,
      gapRemaining,
      avgScore
    };
  });

  // Select best combination
  let bestCombination = null;
  const negativeGapCombinations = combinationsWithMetrics.filter(c => c.gapRemaining < 0);
  
  if (negativeGapCombinations.length > 0) {
    // Prefer negative gap closest to 0
    negativeGapCombinations.sort((a, b) => {
      const gapDiff = b.gapRemaining - a.gapRemaining; // b - a because we want closest to 0 (less negative)
      if (Math.abs(gapDiff) < 0.20) {
        return b.avgScore - a.avgScore; // If gap difference < 0.20, prefer higher score
      }
      return gapDiff;
    });
    bestCombination = negativeGapCombinations[0].combination;
  } else {
    // Minimize positive gap
    combinationsWithMetrics.sort((a, b) => {
      const gapDiff = a.gapRemaining - b.gapRemaining;
      if (Math.abs(gapDiff) < 0.20) {
        return b.avgScore - a.avgScore; // If gap difference < 0.20, prefer higher score
      }
      return gapDiff;
    });
    bestCombination = combinationsWithMetrics[0].combination;
  }

  return bestCombination;
}
